import UAParser from 'ua-parser-js';
import semverCompare from 'semver-compare';
import { getCurrentPathname } from './window';

const MS_EDGE = 'edge';
const DEFAULT_SUPPORTED_BROWSERS_CONFIG = {
  desktop: [
    {
      browser: 'firefox',
      minversion: 41,
    },
    {
      browser: 'ie',
      versions: [11, MS_EDGE],
    },
    {
      browser: 'chrome',
      minversion: 45,
    },
    {
      browser: MS_EDGE,
    },
    {
      os: 'mac os',
      minos: '10.10.0',
      browser: 'safari',
      minversion: 8,
    },
  ],
  tablet: [
    {
      os: 'ios',
      minos: '9',
      browser: 'mobile safari',
    },
    {
      os: 'android',
      minos: '5.0',
      browser: 'chrome',
    },
    {
      browser: 'ie',
      versions: [11, MS_EDGE],
    },
    {
      browser: MS_EDGE,
    },
  ],
  mobile: [
    {
      os: 'ios',
      minos: '9',
      browser: 'mobile safari',
    },
    {
      os: 'ios',
      minos: '5.0',
      browser: 'chrome',
    },
    {
      os: 'android',
      minos: '5.0',
      browser: 'chrome',
      minversion: 50,
    },
  ],
};

const SUPPORTED_CRAWLERS_UA = [
  'Twitterbot/1.0',
  'Mozilla/5.0 (compatible; Twitterbot/1.0)',
  'Mozilla/5.0 (Twitterbot/0.1)',
  'Facebot',
  'Twitterbot',
  'Prerender',
  'HeadlessChrome',
  'facebookexternalhit/1.1',
];

const SUPPORTED_IN_APP_BROWSER_UA = ['Instagram', 'FBAN', 'FBAV', 'Twitter'];

export default class UnsupportedBrowserDetection {
  parser = new UAParser();

  constructor(config = DEFAULT_SUPPORTED_BROWSERS_CONFIG, isInAppBrowserSupported = true) {
    this.supportedBrowsersConfig = config;
    this.isInAppBrowserSupported = isInAppBrowserSupported;
  }

  static isEdge() {
    const { name = '' } = new UAParser().getBrowser();
    return name.toLowerCase() === MS_EDGE;
  }

  static isSafari() {
    const { name = '' } = new UAParser().getBrowser();

    return name.toLowerCase() === 'safari' || name.toLowerCase() === 'mobile safari';
  }

  get isInAppBrowser() {
    return SUPPORTED_IN_APP_BROWSER_UA.some(ua => this.ua.includes(ua));
  }

  get isAppInKioskRoute() {
    return getCurrentPathname().includes('/kiosk');
  }

  get device() {
    return this.parser.getDevice();
  }

  get ua() {
    return this.parser.getUA();
  }

  get browser() {
    return this.parser.getBrowser();
  }

  get os() {
    return this.parser.getOS();
  }

  get deviceType() {
    const { type = 'desktop' } = this.device;
    return type;
  }

  get isCrawler() {
    return SUPPORTED_CRAWLERS_UA.some(ua => this.ua.includes(ua));
  }

  compareVersions(a, b) {
    if (typeof a === 'string' || a instanceof String) {
      return semverCompare(a, b) <= 0;
    }

    return a <= parseInt(b, 10);
  }

  isSupported() {
    document.body.setAttribute('ua', this.ua);

    if (this.isAppInKioskRoute) {
      return true;
    }

    if (this.isInAppBrowser) {
      return this.isInAppBrowserSupported;
    }

    if (this.isCrawler) {
      return true;
    }

    const { version: browserVersion } = this.browser;

    return !this.supportedBrowsersConfig[this.deviceType].every(options => {
      const { os, minos, browser, minversion, versions } = options;
      const parsedVersion = isNaN(parseInt(browserVersion, 10))
        ? browserVersion.toLocaleLowerCase()
        : parseInt(browserVersion, 10);

      const checked = {
        os: os === this.os.name.toLowerCase(),
        minos: this.compareVersions(minos, this.os.version),
        browser: browser === this.browser.name.toLowerCase(),
        minversion: this.compareVersions(minversion, browserVersion),
        versions: versions ? versions.indexOf(parsedVersion) >= 0 : false,
      };

      return (
        Object.keys(options)
          .map(key => checked[key])
          .indexOf(false) !== -1
      );
    });
  }
}
